import React, { useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
    ComboBox,
    ListBox,
    ListBoxItem,
    ListBoxSection,
} from 'react-aria-components';

import {
    SearchField,
    TextField,
    WDSModal,
} from 'wanderu-components';

import styles from './SearchInput.scss';

const renderItem = ({ href, Icon, name }) => (
    <ListBoxItem
        className={styles.item}
        href={href}
        textValue={name}
    >
        {Icon && <Icon className={styles.itemIcon} />}
        {name}
    </ListBoxItem>
);

const renderSection = ({ children, id }) => (
    <>
        {children && <ListBoxSection className={styles.section} id={id} items={children}>
            {renderItem}
        </ListBoxSection>}
    </>
);

const SearchInput = ({
    backText,
    clearText,
    footer,
    Icon,
    inputValue = '',
    items,
    name,
    onFocus,
    onInputChange,
    onSelectionChange,
    placeholder,
}) => {
    const [selectedKey, setSelectedKey] = useState(null);
    const [isOpen, setOpen] = useState(false);

    const handleSelectionChange = (id) => {
        if (!id) return;
        setSelectedKey(id);
        onSelectionChange(id);
        setOpen(false);
    };

    const openModal = () => {
        if (!isOpen) {
            setOpen(true);
        }
    };

    const handleInputChange = (value) => {
        setSelectedKey(prevState => (value === '')
            ? null
            : prevState);

        onInputChange(value);
        openModal();
    };

    const handleFocus = (e) => {
        const value = e?.target?.value;

        if (value && !items) {
            onInputChange(value);
        }
        onFocus(e);
    };

    const handleKeyDown = (e) => {
        const { key } = e;

        if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Home', 'End'].includes(key)) {
            openModal();
        }
    };

    const inputContainerStyles = classNames(styles.inputContainer, {
        [styles.left]: name === 'origin',
        [styles.right]: name === 'destination',
    });

    return (
        <>
            <TextField
                Icon={Icon}
                classes={{
                    inputContainer: inputContainerStyles,
                }}
                name={name}
                onChange={handleInputChange}
                onClick={openModal}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                value={inputValue}
            />
            <WDSModal isOpen={isOpen} onOpenChange={setOpen} isDismissable>
                <ComboBox
                    data-id={`${name}SearchInput`}
                    inputValue={inputValue}
                    items={items}
                    menuTrigger="focus"
                    onFocus={handleFocus}
                    onInputChange={handleInputChange}
                    onSelectionChange={handleSelectionChange}
                    selectedKey={selectedKey}
                    allowsCustomValue
                    autoFocus
                    isOpen
                    shouldFocusWrap
                >
                    <SearchField
                        backText={backText}
                        clearText={clearText}
                        closeModal={() => setOpen(false)}
                        inputValue={inputValue}
                        onClear={() => handleInputChange('')}
                        placeholder={placeholder}
                    />
                    <ListBox className={styles.listBox}>
                        {item => (item?.type === 'section')
                            ? renderSection(item)
                            : renderItem(item)
                        }
                    </ListBox>
                </ComboBox>

                {footer}
            </WDSModal>
        </>
    );
};

SearchInput.propTypes = {
    backText: PropTypes.string,
    clearText: PropTypes.string,
    footer: PropTypes.node,
    Icon: PropTypes.elementType,
    inputValue: PropTypes.string,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string,
        })
    ),
    name: PropTypes.string,
    onFocus: PropTypes.func,
    onInputChange: PropTypes.func,
    onSelectionChange: PropTypes.func,
    placeholder: PropTypes.string,
};

export default SearchInput;
